import { ComponentRef, Ref } from "react"
import { usePublicUsers } from "@/hooks/use-public-users"
import { CurrentUser, PublicUser, UserRef } from "@/types/user"
import { cn } from "cn"

import cloudflareLoader from "@/lib/cloudflare-loader"
import { browserToday } from "@/lib/date-utils"
import { useAppSelector } from "@/lib/storage/hooks"
import { selectCurrentUserHandle, selectUser } from "@/lib/storage/user-slice"
import { avatarColorPair, profilePath, userDisplayName } from "@/lib/user-utils"
import IconProfile from "@/components/icons/profile"
import { Avatar, AvatarFallback, AvatarImage, AvatarProps } from "./ui/avatar"
import { HoverCardContentProps } from "./ui/hover-card"
import { Link } from "./ui/link"
import { Skeleton } from "./ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipContentProps,
  TooltipTrigger,
} from "./ui/tooltip"
import { UserHoverCard } from "./user-hover-card"
import { UserStatusDot } from "./user-status-dot"

export interface UserAvatarProps extends AvatarProps {
  user?: CurrentUser | PublicUser
  userRef?: UserRef
  inviteEmail?: string
  className?: string
  active?: boolean
  tooltip?: boolean | TooltipContentProps
  linked?: boolean
  hoverCard?: boolean | HoverCardContentProps
}

export function UserAvatar({
  ref,
  size = "sm",
  user,
  userRef,
  inviteEmail,
  className,
  active,
  tooltip,
  linked,
  hoverCard,
  ...props
}: UserAvatarProps & { ref?: Ref<ComponentRef<typeof Avatar>> }) {
  const currUser = useAppSelector(selectUser)

  const { users, ref: usersRef } = usePublicUsers(userRef ? [userRef] : [])
  const userFromRef = userRef
    ? users.find((u) => u.user_ref === userRef)
    : undefined
  // This is only for anonymous of currUser.  We can't use this as we pass in
  // user to display.
  // const isAnonymous = useAppSelector(selectUserIsAnonymous)
  const avatarUser = user || userFromRef || (userRef ? null : currUser)
  // Render anonymous sharers and the current anonymous user without profile affordances.
  const isAnonymous =
    !!avatarUser &&
    (("anonymous" in avatarUser && avatarUser.anonymous === true) ||
      ("provider" in avatarUser && avatarUser.provider === "anonymous"))
  // image_url is the raw upload (a 1.5k-square JPEG can be 700 KB) drawn at 16-144
  // CSS px. One 256 px webp per user covers every size through xl at 3x and stays a
  // single cache entry across sizes; the loader also resolves /ugc paths to the CDN.
  const userImageUrl = avatarUser?.image_url
    ? cloudflareLoader({ src: avatarUser.image_url, width: 256 })
    : undefined

  const handle = avatarUser?.handle || "Traveler"
  const userName = avatarUser?.first_name || handle
  const showInitials =
    !!(avatarUser?.first_name && avatarUser?.last_name) && size !== "3xs"

  const avatarIdentifier =
    inviteEmail || (isAnonymous ? `${browserToday().day}` : handle)
  const colorPair = avatarColorPair(avatarIdentifier)
  const avatarRef = userRef ? usersRef : ref
  const currUserHandle = useAppSelector(selectCurrentUserHandle)

  const renderAvatar = () => {
    if (!avatarUser) {
      return (
        <Avatar size={size} className={className} {...props} ref={avatarRef}>
          <Skeleton className="size-full rounded-full" />
        </Avatar>
      )
    }

    if (inviteEmail || isAnonymous) {
      return (
        <Avatar
          size={size}
          className={cn("items-center justify-center", colorPair, className)}
          {...props}
          ref={avatarRef}
        >
          <IconProfile strokeWidth={2} className="text-[1.25em]" />
        </Avatar>
      )
    }

    return (
      <Avatar size={size} className={className} {...props} ref={avatarRef}>
        <span
          className={cn(
            "size-full overflow-hidden rounded-full",
            active !== undefined &&
              "[mask:radial-gradient(circle_at_86.5%_86.5%,transparent_0,transparent_10%,black_11%)]"
          )}
        >
          <AvatarImage
            src={userImageUrl}
            alt={userName}
            className="dark:bg-white"
          />
          {userName && (
            <AvatarFallback className={cn("no-translate", colorPair)}>
              {showInitials
                ? `${avatarUser?.first_name?.charAt(0)}${avatarUser?.last_name?.charAt(0)}`
                : userName.charAt(0)}
            </AvatarFallback>
          )}
        </span>
        {active !== undefined && (
          <UserStatusDot
            active={active}
            className="absolute right-[4.5%] bottom-[4.5%] size-[18%]"
          />
        )}
      </Avatar>
    )
  }

  let avatar = renderAvatar()

  if (
    linked &&
    avatarUser &&
    !inviteEmail &&
    !isAnonymous &&
    avatarUser.handle
  ) {
    avatar = <Link href={profilePath(avatarUser as PublicUser)}>{avatar}</Link>
  }

  if (tooltip && avatarUser) {
    const displayName =
      avatarUser.handle === currUserHandle ? "You" : userDisplayName(avatarUser)
    avatar = (
      <Tooltip key={avatarUser.user_ref}>
        <TooltipTrigger render={avatar} />
        <TooltipContent {...(typeof tooltip === "object" ? tooltip : {})}>
          <span className="no-translate">{displayName}</span>
        </TooltipContent>
      </Tooltip>
    )
  } else if (hoverCard && avatarUser?.user_ref) {
    avatar = (
      <UserHoverCard
        userRef={avatarUser.user_ref}
        {...(typeof hoverCard === "object" ? hoverCard : { side: "top" })}
      >
        {avatar}
      </UserHoverCard>
    )
  }

  return avatar
}

UserAvatar.displayName = "UserAvatar"
